import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { fromEvent, Observable, ReplaySubject, Subject } from 'rxjs';
import { first, switchMap, filter, share, map, distinctUntilChanged, skip } from 'rxjs/operators';

import { Map, MapMouseEvent } from 'mapbox-gl';
import mapboxDirections, { DirectionsResponse } from '@mapbox/mapbox-sdk/services/directions';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { cleanCoords, Feature, featureCollection, lineString, LineString, MultiLineString, point, Point, Position, distance, FeatureCollection } from '@turf/turf';

import { AutoSub, AutoSubOnDestroy, Coords, FeatureWaypoint, HotkeysService, MapService, Mote, MoteData, MoteService, MotesPaths, MoteType, MoteWaypoint, ProjectService } from '@motes-sdk';
import { GeoSearchService } from '@motes-sdk/components/geo-search/geo-search.service';
import { SelectMode, WaypointMode, styles, isFeaturePoint, isFeatureWaypoint, positionToTuple, MAP_EDITOR_LAYERS, layers } from '../utils';
import { MapEditorEvent, MapEditorEventType, SetMapSearchEvent, SiblingStories, WaypointClickedEvent, WaypointDeletedEvent, WaypointMovedEvent } from '../models/map-editor-event';

/**
 * Provides functionality for map-related editing
 */
@AutoSubOnDestroy()
@Injectable({
    providedIn: 'root',
})
export class MapEditorService implements OnDestroy {
    /** Max distance to snap waypoints to road / route before marking it off-road */
    private readonly WAYPOINT_SNAP_DISTANCE = 50;
    /** The Mapbox map used throughout the application */
    private map: Map = null;
    /** Mapbox plugin used for modifying data on the map */
    private draw: MapboxDraw = null;
    /** Flag to determine whether `MapEditorService` has been initialized */
    private initialized: boolean = false;
    /** ReplaySubject keeping track of whether stories are loaded on the map */
    public loaded: ReplaySubject<boolean> = new ReplaySubject(1);
    /** The active tour */
    private tour: Mote;
    /** All stories from the active tour */
    private stories: Mote[] = null;
    /** Key for the currently active story */
    private activeStoryKey: string = null;
    /** Subject for aggreating all `MapEditorEvent` objects */
    private mapEditorEventSubject: Subject<MapEditorEvent> = new Subject<MapEditorEvent>();
    /** True if a story is being dragged, false otherwise */
    private storyMoving: boolean = false;
    /** Id of active waypoint */
    private activeWaypoint: string;
    /** Id of waypoint being dragged */
    private waypointMoving: string;

    private mouseIsDragging: boolean = false;

    constructor(
        private mapService: MapService,
        private moteService: MoteService,
        private projectService: ProjectService,
        private router: Router,
        private hotkeys: HotkeysService,
        private geoSearchService: GeoSearchService,
    ) { }

    /**
     * Initialize `MapEditorService`:
     * Instantiates Mapbox Draw, adds map event listeners, once the map has been initialized.
     */
    public async init() {
        // Prevent initialization from running more than once
        if(this.initialized) return;
        this.initialized = true;

        // Await initialization of the map
        await this.mapService.awaitInit();
        this.map = this.mapService.map;
        // Instantiate Mapbox Draw
        this.draw = new MapboxDraw({
            defaultMode: 'select',
            // Adds custom modes to the built-in set of modes
            modes: {
                waypoint: WaypointMode,
                select: SelectMode,
            },
            userProperties: true, // Allow user properties on features for styling
            displayControlsDefault: false, // Do not display the defualt controls for mapbox-gl-draw
            styles, // Load custom styles
        });
        // Add Draw control to the map
        this.map.addControl(this.draw);
        // Show stories on map
        this.mapService.showLayer('storyClickLayer');

        // Set up event listeners and handlers
        this.addMapEventListeners();
        this.watchTourChange();
        this.watchStories();
        this.watchActiveStory();
        this.watchFirstStory();
        this.watchLastStory();
        this.watchInboundEditorEvents();
        this.registerHotkeys();
        this.watchMouseDown();
        this.watchMouseUp();
        this.watchFeatureDrag();
        this.watchCursor();
    }

    private watchCursor() {
        // Map drag start
        AutoSub(this).reg['dragStartSub'] = fromEvent(this.map, 'dragstart').subscribe(() => {
            this.mouseIsDragging = true;
            this.map.getCanvas().style.cursor = 'grabbing';
        })
        // Map drag end
        AutoSub(this).reg['dragEndSub'] = fromEvent(this.map, 'dragend').subscribe(() => {
            this.mouseIsDragging = false;
            this.map.getCanvas().style.cursor = 'default';
        })
        // Large waypoint enter/exit
        AutoSub(this).reg['waypointHoverSub'] = fromEvent(this.map, 'mousemove').subscribe((ev: MapMouseEvent) => {
            if(this.mouseIsDragging) return;
            const waypointsHovered = this.map.queryRenderedFeatures(ev.point, { layers: [...layers.waypoint.hover, ...layers.waypoint.active] });
            this.map.getCanvas().style.cursor = (waypointsHovered.length > 0) ? 'grab' : 'default';
        })
        // Mouse release
        AutoSub(this).reg['mouseReleaseSub'] = fromEvent(this.map, 'mouseup').subscribe((ev: MapMouseEvent) => {
            this.mouseIsDragging = false;
            const waypointsReleased = this.map.queryRenderedFeatures(ev.point, { layers: [...layers.waypoint.inactive, ...layers.waypoint.active] }).length > 0;
            this.map.getCanvas().style.cursor = waypointsReleased ? 'grab' : 'default';
        })
    }

    private watchFeatureDrag() {
        AutoSub(this).reg['featureDragSub'] = this.mapService.watchFeatureDrag(MAP_EDITOR_LAYERS).subscribe((ev) => {
            // Set grabbing cursor
            this.mouseIsDragging = true;
            this.map.getCanvas().style.cursor = 'grabbing';
            // Keep track of waypoints being dragged
            const feature = ev.features[0];
            this.waypointMoving = feature?.properties?.user_meta == 'waypoint' ? String(feature.id) : null;
            // Remove waypoint tooltip if any
            if(this.waypointMoving) {
                const clickEvent = new WaypointClickedEvent(null, null, null, null);
                this.sendMapEditorEvent(clickEvent);
            }
        })
    }

    private watchMouseDown() {
        AutoSub(this).reg['mouseDownSub'] = this.mapService.watchMapClick(MAP_EDITOR_LAYERS).subscribe((ev) => {
            // Keep track of currently active waypoint
            const selectedFeatures = this.draw.getSelected().features;
            const selectedWaypoint = selectedFeatures.find((feature) => feature.properties.meta == 'waypoint');
            this.activeWaypoint = selectedWaypoint?.id?.toString();
        })
    }

    /**
     * Watch map click events from all editor layers.
     * Check if pin should be added or removed
     */
    private watchMouseUp() {
        AutoSub(this).reg['mouseUpSub'] = this.mapService.watchMapRelease(MAP_EDITOR_LAYERS).subscribe(async (ev) => {
            // If map or story is being dragged
            if(this.storyMoving || ev.mapMoved) return;

            const emptyMapClicked = !ev.featureClicked;
            const storyClicked = !emptyMapClicked && ev.features.some((feature) => feature['layer']['id'] == 'storyClickLayer');
            const waypointClicked = !storyClicked ? ev.features.find((feature) => feature.properties?.user_meta == 'waypoint') : null;

            // Waypoint drag release
            if(waypointClicked && this.waypointMoving) return this.waypointMoving = null;

            // Waypoint click
            if(waypointClicked) {
                // Clear search and remove pin if any
                const searchEvent = new SetMapSearchEvent('', false);
                this.sendMapEditorEvent(searchEvent);
                // Add waypoint tooltip
                const clickEvent = new WaypointClickedEvent(waypointClicked.properties?.id, waypointClicked.geometry['coordinates'], waypointClicked.properties?.user_isSnapped, waypointClicked.properties?.user_storyKey);
                this.sendMapEditorEvent(clickEvent);
                return;
            }

            // Story click or empty map click when a waypoint is selected
            if(storyClicked || this.activeWaypoint) {
                // Clear search and remove pin if any
                const searchEvent = new SetMapSearchEvent('', false);
                this.sendMapEditorEvent(searchEvent);
                return;
            }

            // Empty map click
            if(emptyMapClicked) {
                // If story pane is open, go to 'Stories'
                if(this.activeStoryKey) return this.router.navigate(['editor', this.tour.project, this.tour.key, 'stories']);
                // Otherwise place pin
                const searchEvent = new SetMapSearchEvent(ev.position);
                this.sendMapEditorEvent(searchEvent);
            }
        })
    }

    /**
     * Listen to incoming editor story events
     */
    private watchInboundEditorEvents() {
        AutoSub(this).reg['inboundEventSub'] = this.watchMapEditorEvents().subscribe(async ev => {
            switch(ev.type) {
                case MapEditorEventType.STORY_DELETED:
                    // Delete the story and update route of siblings
                    this.deleteStory(ev.storyKey);
                    break;
                case MapEditorEventType.STORY_CREATED:
                    // Connect previous and the new story
                    await this.updateRouteBetweenStories(ev.previousStory, ev.story);
                    await this.updateTourProperties();
                    break;
                case MapEditorEventType.STORY_ORDER_CHANGED:
                    // Update routes on reordered stories
                    this.swapStoryOrder(ev.storiesSwapped);
                    break;
                case MapEditorEventType.STORY_DRAG_END:
                case MapEditorEventType.MOVE_STORY:
                    this.storyMoving = false;
                    this.moveStory(ev.storyKey, ev.newPosition);
                    break;
                case MapEditorEventType.STORY_DRAG_START:
                    this.changeToSelectMode();
                    this.storyMoving = true;
                    break;
            }
        });
    }

    /**
     * Delete story and update route between direct siblings
     */
    private async deleteStory(storyKey: string) {
        // Find the story in the stories array
        const { previousStory, nextStory } = this.getSiblingStories(storyKey);
        // Update route between the delete story siblings
        this.updateRouteBetweenStories(previousStory, nextStory);
        // Finally delete the story
        await this.moteService.deleteMote(storyKey);
        // Update combined route and related tour properties
        await this.updateTourProperties();
    }

    /**
     * Calculate new routes as needed for swapped / reordered stories
     */
    private async swapStoryOrder(swappedStories: SiblingStories[]): Promise<any> {
        // Recalculate route between all swapped stories
        const updateRoutes = swappedStories.map(swapped => {
            return Promise.all([
                // Update route between previous story and story
                this.updateRouteBetweenStories(swapped.previousStory, swapped.story),
                // Update route between story and next story
                this.updateRouteBetweenStories(swapped.story, swapped.nextStory)
            ]);
        });
        await Promise.all(updateRoutes);
        await this.updateTourProperties();
    }

    /**
     * Find the story and direct siblings (stories connected) to the story (if the story has any direct siblings)
     */
    private getSiblingStories(storyKey: string): SiblingStories {
        // Find the story in the stories array
        const storyIndex = this.stories.findIndex(story => story.key == storyKey);
        // Find the story and stories connected to this story (if the story has any siblings)
        const story = this.stories[storyIndex];
        const previousStory = this.stories[storyIndex - 1];
        const nextStory = this.stories[storyIndex + 1];
        return { story, previousStory, nextStory };
    }

    /**
     * Update route from story A to story B
     */
    private async updateRouteBetweenStories(storyA: Mote, storyB: Mote, waypoints: MoteWaypoint[] = [], keepSnapSetting: boolean = false) {
        // Do nothing if there is no story B to route to - or in case no stories left at all (nothing to connect)
        if(!storyB || (!storyA && !storyB)) return;

        // Clear the route from A to B in case story A does not exist (story B is the first story)
        if(!storyA) {
            // Update story B's geometry
            storyB.routeGeometry = [];
            storyB.waypoints = [];
        }

        // In case both stories exist, update the route between them
        if(storyA && storyB) {
            // Filter out waypoints that should not snap to automatic generated route
            // OBS: Off-road waypoints are included in the first directions lookup event though they might be marked off road-later
            // and this can generate extra route segments until the off-road waypoints are moved or other points are moved, however the alternative
            // is to make an extra directions lookup immediately when we detect a off-road waypoint and this seems like overkill for now.
            const snapWaypoints = waypoints.filter( point => point.isSnapped);
            // Find route between story A and B
            const directions = await this.getDirections(storyA.position.longLat(), storyB.position.longLat(), snapWaypoints.map(w => w.location));
            let routeSegment = this.getRouteSegmentFromDirectionsResponse(directions);
            // Print error if it is not possible to find a route between A and B and clear any old route
            if(!routeSegment) console.error(`Could not generate route segment between ${storyA.key} and ${storyB.key}`);

            // Get the waypoint locations from the directions response (i.e. snapped to the Mapbox road and path network)
            const snappedWaypoints = (waypoints.length > 0) ? this.getWaypointsFromDirectionsResponse(directions) : [];
            // Map each snapped waypoint with ids of MoteWaypoint
            snapWaypoints.forEach((waypoint, index) => {
                if(!keepSnapSetting) {
                    // Calculate distance from snapped waypoint to user way point
                    const snapDistance = distance( waypoint.location, snappedWaypoints[index], { units: 'meters' });
                    // Snap point if it is close enough to the found route / road and save it in waypoint
                    // OBS: Waypoints will never be snapped again when they have been marked off-road once
                    waypoint.isSnapped = Boolean(snapDistance <= this.WAYPOINT_SNAP_DISTANCE);
                }
                // Snap way point
                if(waypoint.isSnapped) waypoint.location = snappedWaypoints[index];
            });

            // Update story B's geometry
            storyB.routeGeometry = this.getOffRoadWaypointRoute(waypoints, routeSegment) || [];
            storyB.waypoints = waypoints;
        }

        // Save story B's geometry to database
        return Promise.all([
            this.moteService.saveMoteRouteGeometry(storyB.key, storyB.routeGeometry),
            this.moteService.saveMoteWaypoints(storyB.key, storyB.waypoints)
        ]);
    }

    /**
     * Add off-road waypoints (that does not snap to the generated route) and remove the auto generated route around them.
     * @param waypoints All waypoints (snapped and non snapped)
     * @param routeSegment Currently auto generated route
     * @return A route (segment) that includes any non snapped / off-road waypoints
     */
    private getOffRoadWaypointRoute(waypoints: MoteWaypoint[], routeSegment: [number, number][]): [number, number][] {
        waypoints.forEach((waypoint, index) => {
            if(waypoint.isSnapped) return;
            // If point should not be snapped, delete route geometry between previous and next waypoint (create a straight line)
            // Find previous and next waypoint in the list
            const previousWaypointLocation = waypoints[index - 1]?.location;
            const nextWaypointLocation = waypoints[index + 1]?.location;

            // Find intersection point with route (or use first segment if this is first waypoint)
            let previousSegmentIndex = (previousWaypointLocation) ? this.findIntersectionIndex(routeSegment, previousWaypointLocation) : 0;
            // In case this is the last waypoint, use the last route segment, else find intersection point with route
            let nextSegmentIndex = (nextWaypointLocation) ? this.findIntersectionIndex(routeSegment, nextWaypointLocation) : routeSegment.length - 1;

            if(previousSegmentIndex >= 0 && nextSegmentIndex >= 0) {
                // Remove route between off road waypoint and surrounding snapped waypoints
                routeSegment.splice(previousSegmentIndex + 1, (nextSegmentIndex - previousSegmentIndex) - 1);
            }

            // Push the off-road waypoint in between the waypoints on the route to make a direct route
            routeSegment.splice(previousSegmentIndex + 1, 0, waypoint.location);
        });
        return routeSegment;
    }

    private findIntersectionIndex(routeSegment: [number, number][], point: [number, number]) {
        if(!point) return -1;

        return routeSegment.findIndex( routePoint => {
            return (routePoint[0] == point[0] && routePoint[1] == point[1]);
        });
    }

    /**
     * Watch map editor events to and from the map editor
     */
    public watchMapEditorEvents(): Observable<MapEditorEvent> {
        return this.mapEditorEventSubject.pipe(share());
    }

    /**
     * Send a map editor event to listeners
     */
    public sendMapEditorEvent(event: MapEditorEvent): void {
        this.mapEditorEventSubject.next(event);
    }

    /**
     * Add event listeners for all map interactions
     */
    private addMapEventListeners(): void {
        // Handle creation of waypoints
        this.map.on('draw.create', async (event) => {
            // A `draw.create` event always contains at least one feature
            const feature = event.features[0];

            if(isFeatureWaypoint(feature)) {
                this.updateStoryWaypoints(feature.properties.storyKey, event.waypoints);
            }
        });

        // Moving stories and waypoints
        this.map.on('draw.update', (event) => {
            // Only handle `draw.update` events with `move` actions
            if(event.action != 'move') return;
            // Prevent update events associated with multiple features
            if(event.features.length > 1) return console.error('You are trying to update more than one feature. This is currently not supported.');
            // A `draw.update` event always contains at least one feature
            const feature = event.features[0];

            if(isFeatureWaypoint(feature)) {
                this.moveWaypoint(feature);
            }
        }
        );
    }

    /**
     * TODO: Remove all event listeners added with `addMapEventListeners()`.
     * ! Draw events are not typed
     */
    private removeMapEventListeners(): void { }

    /**
     * Watch the active tour and fit map bounds to stories upon initialization
     */
    private watchTourChange(): void {
        AutoSub(this).reg['activeTour'] = this.projectService.watchActiveProject().pipe(
            // Do nothing if story did not change or no project is active
            filter((ev) => ev.routeChanged && Boolean(ev.project)),
            // Get stories of active project
            switchMap(() => this.projectService.watchActiveTourDetails().pipe(first())),
        ).subscribe((details) => {
            const stories = details?.children;
            const tour = details?.tour;
            if(!details || !tour || !stories || stories.length <= 0) return;

            // Generate route segments for all stories, if not currently present
            // Disregard the first story as this should not contain `routeGeometry` data
            // TODO: This is only relevant for tours made in Studio. Remove, once Studio has been deprecated.
            const tourTypeHasRoute = (tour.type == MoteType.ROUTE || tour.type == MoteType.QUEST);
            const tourRoute = tour.getRoute();
            if(tourTypeHasRoute && tourRoute && tourRoute.length > 0 && stories.slice(1).find((story) => !story.routeGeometry)) {
                console.warn('Auto-updating order and route segments for all stories of tour:', tour);
                this.moteService.autoUpdateMoteOrder(tour);
            }
        });
    }

    /**
     * Fly to a position on the map.
     * The method assumes that the editor menu and story panes are visible – and adds corresponding padding to the map bounds.
     * @param position The position
     */
    private flyToPosition(position: Position): void {
        if(!position[0] || !position[1]) return;
        // Convert `Position` to `LngLatLike` to comply with `Map.fitBounds()`
        const storyCoordsLngLatLike = { lng: position[0], lat: position[1] };

        // Show story in the visible space next to story pane
        // If window is too small for any such space, do nothing
        const padding = { top: 200, bottom: 200, left: 1300, right: 10 };
        const maxZoom = this.map.getZoom();
        if(window.innerWidth <= (padding.left + padding.right) || window.innerHeight <= (padding.top + padding.bottom)) return;
        this.map.fitBounds([storyCoordsLngLatLike, storyCoordsLngLatLike], { padding, maxZoom, speed: 0.8 });
    }

    /**
     * Watch the active story and fly to it
     */
    private watchActiveStory(): void {
        AutoSub(this).reg['activeStory'] = this.projectService.watchActiveStory().subscribe((story) => {
            this.activeStoryKey = story?.key;
            if(!story) return;
            this.flyToPosition([story.position.long, story.position.lat]);
        });
    }

    /**
     * Watch address, city and country changes in first story and update tour's start place, city and country
     */
    private watchFirstStory(): void {
        // TODO: This is problematic as it saves the tour based on a change in the tour
        //       Fix the issue of saving city country and start place in another way
        //       For now we just save the tour details once when publishing the tour or manually set it on the tour "presentation" page 
        //       Why don't we just juse  MapEditorEventType.MOVE_STORY, STORY_DELETED,  STORY_CREATED, STORY_ORDER_CHANGED, events to do this??

        // AutoSub(this).reg['firstStory'] = this.projectService.watchActiveTourDetails().pipe(
        //     map((ev) => {
        //         const firstStory = ev?.children.find((child) => child.order == 1);
        //         return { tourKey: firstStory?.parent, address: firstStory?.address || '', city: firstStory?.city || '', country: firstStory?.country || '' };
        //     }),
        //     distinctUntilChanged((prev, curr) => {
        //         console.log("Prev address / curr address", prev.address, curr.address)
        //         return (prev.address == curr.address) && (prev.city == curr.city) && (prev.country == curr.country);
        //     }),
        //     skip(1),
        // ).subscribe(({tourKey, address, city, country}) => {
        //     if(!tourKey) return;
        //     console.log("Save mote start place now!!!");
        //     this.moteService.saveMoteStartPlace(tourKey, address);
        //     this.updateMoteCityAndCountry(tourKey, city, country);
        // });
    }

    /**
     * Watch address changes in the last story and update tour's endPlace
     */
    private watchLastStory(): void {
        // TODO: See watchFirstStory to figure why this is a bad solution of saving mote end place
        // AutoSub(this).reg['lastStory'] = this.projectService.watchActiveTourDetails().pipe(
        //     map((ev) => {
        //         const lastStory = ev?.children.find((child) => child.order == ev.children.length);
        //         return { tourKey: lastStory?.parent, address: lastStory?.address };
        //     }),
        //     distinctUntilChanged((prev, curr) => {
        //         return prev.address == curr.address;
        //     }),
        //     skip(1),
        // ).subscribe(({ tourKey, address }) => {
        //     if(!tourKey) return;
        //     this.moteService.saveMoteEndPlace(tourKey, address);
        // });
    }

    /**
     * Watch stories from the active route and add them as Mapbox Draw features on the map
     */
    private watchStories(): void {
        AutoSub(this).reg['activeStories'] = this.projectService.watchActiveTourDetails().subscribe((tourDetails) => {
            const tour = this.tour = tourDetails?.tour;
            const stories = this.stories = tourDetails?.children;
            this.loaded.next(false);

            // If there is no active motes or project yet
            if(!tour || !stories || stories.length <= 0) {
                this.setFeatures(featureCollection([]));
                this.mapService.setStories([]);
                this.loaded.next(true);
                return;
            }

            // Place stories on map
            this.mapService.setStories(stories);
            this.moteService.saveMoteStoryCount(tour.key, stories.length);

            // Get route segments and waypoints if this type of tour has routes
            const tourTypeHasRoute = (tour.type == MoteType.ROUTE || tour.type == MoteType.QUEST);
            const allWaypoints = (tourTypeHasRoute) ? stories.reduce((allWaypoints: FeatureWaypoint[], story) => allWaypoints.concat(this.getWaypointsFromStory(story)), []) : [];
            const allRouteSegments = (tourTypeHasRoute) ? this.getRouteSegmentsFromStories(stories) : [];
            const allFeatures: Feature<Point | LineString>[] = [...allWaypoints, ...allRouteSegments];

            // Add marker, if currently present in Draw's data store
            const marker = this.draw.get('marker');
            if(marker && isFeaturePoint(marker)) allFeatures.push(marker);

            let collection = featureCollection(allFeatures);

            // Replace all the current features in Draw´s data store with the ones contained in the `FeatureCollection`
            // https://github.com/mapbox/mapbox-gl-draw/blob/main/docs/API.md#setfeaturecollection-featurecollection-arraystring
            this.draw.set(collection);
            this.loaded.next(true);
        });
    }

    private setFeatures(collection: FeatureCollection<any>) {
        this.draw.set(collection);
        this.changeToSelectMode();
    }

    public changeToSelectMode() {
        if(!this.draw) return;
        this.draw.changeMode('select');
        if(this.mouseIsDragging) return;
        this.map.getCanvas().style.cursor = 'default';
    }

    /**
     * Display a marker on the map at a given position.
     * The marker remains on the map until explicitly removed by `removeMarker` – or the tour changes.
     * @param position The position
     */
    public addMarker(position: Coords): void {
        if(!this.draw) return console.warn('Cannot add marker: Mapbox Draw has not yet been instantiated.');
        if(!position) return console.warn("Marker position is undefined, skip adding marker");

        const markerFeature = point(position.longLat(), { meta: 'marker' }, { id: 'marker' });
        this.draw.add(markerFeature);
    }

    /**
     * Remove a marker feature which has been added using `addMarker`
     */
    public removeMarker(): void {
        // Don't remove marker when Mapbox Draw has not yet been instantiated
        if(!this.draw) return;
        this.draw.delete('marker');
    }

    /**
     * Get all waypoints (user-specified points included on the route segment) for a story
     * @param story The story
     * @returns An array of `Waypoint` features
     */
    private getWaypointsFromStory(story: Mote): FeatureWaypoint[] {
        if(!story.waypoints) return [];

        return story.waypoints.map((waypoint) =>
            point(waypoint.location, { meta: 'waypoint', storyKey: story.key, isSnapped: waypoint.isSnapped }, { id: waypoint.id })
        );
    }

    /**
     * Get the route segment for a story (the path leading up to)
     * @param story the story
     * @returns the route segment as a `Feature<LineString>`
     */
    private getRouteSegmentFromStory(story: Mote): Feature<LineString, { meta: 'route-segment', storyKey: string, waypoints: MoteWaypoint[]; }> {
        let coordinates = story.getRoute();
        // A LineString must have at least 2 coords
        if(coordinates.length < 2) return null;
        return lineString(coordinates, { meta: 'route-segment', storyKey: story.key, waypoints: story.waypoints || [] });
    }

    /**
     * Get the route segments for a list of stories (the path leading up to)
     */
    private getRouteSegmentsFromStories(stories: Mote[]): Feature<LineString>[] {
        // Get the route segment for each story and filter out any falsy values
        return stories.map((story) => this.getRouteSegmentFromStory(story)).filter(segment => segment);
    }

    /**
     * Get directions from the [Mapbox Directions API](https://docs.mapbox.com/api/navigation/directions/)
     * @param from The start position
     * @param to The end position
     * @param via Optional array of positions to include in the route segment
     * @returns A route segment with a `[number, number][]` type (to adhere to the `Mote` model)
     */
    private async getDirections(from: [number, number], to: [number, number], via: [number, number][] = []): Promise<DirectionsResponse<LineString | MultiLineString>> {
        const combinedPositions = [from, ...via, to];
        const positionsAsMapboxWaypoints = combinedPositions.map((waypoint) => ({ coordinates: waypoint }));

        const request = mapboxDirections({ accessToken: MotesPaths.MAPBOX_ACCESS_TOKEN }).getDirections({
            profile: 'walking',
            waypoints: positionsAsMapboxWaypoints,
            geometries: 'geojson',
        });

        // The `walkway_bias` query parameter is not included in the API and has to be added manually
        // Adding any query parameter manually will result in overwriting all query parameters set on the original request object
        // Hence, all query parameters have to be set manually
        // The `geometries` parameter has to be included in the original request object as well to enable proper typing of the reponse
        request.query = {
            geometries: 'geojson',
            overview: 'full',
            continue_straight: 'true',
            walkway_bias: 1,
        };

        const response = await request.send();

        // Return the response body as JSON: https://docs.mapbox.com/api/navigation/directions/#response-retrieve-directions
        return response.body;
    }

    /**
     * Extract the route with the highest recommendation rank from a `DirectionsResponse`
     * @param directionsResponse The `DirectionsResponse`
     * @returns The route segment as an array of position tuples
     */
    private getRouteSegmentFromDirectionsResponse(directionsResponse: DirectionsResponse<LineString | MultiLineString>): [number, number][] {
        // With a response code of 'Ok' Mapbox guarantees the presence of a route in the response object: https://docs.mapbox.com/api/navigation/directions/#directions-api-errors
        if(!directionsResponse || directionsResponse?.code != 'Ok') return null;
        const routeGeometry = directionsResponse.routes[0].geometry;
        // According to the API documentation, the route geometry should be `LineString`: https://docs.mapbox.com/api/navigation/directions/#route-object
        // The types, however, says it can potentially be a `MultiLineString`
        if(routeGeometry.type != 'LineString') {
            console.warn('Route in `DirectionsResponse` is of incompatible geometry type:', routeGeometry);
            return null;
        }

        return routeGeometry.coordinates.map((position) => positionToTuple(position));
    }

    /**
     * Extract waypoints, excluding stories, from a `DirectionsResponse`.
     * @param directionsResponse The `DirectionsResponse`
     * @returns The waypoints as an array of position tuples
     */
    private getWaypointsFromDirectionsResponse(directionsResponse: DirectionsResponse<LineString | MultiLineString>): [number, number][] {
        // With a response code of 'Ok' Mapbox guarantees the presence of a route in the response object: https://docs.mapbox.com/api/navigation/directions/#directions-api-errors
        if(!directionsResponse || directionsResponse?.code != 'Ok') return null;
        const waypointsIncludingStories = directionsResponse.waypoints;
        // @ts-ignore
        // The waypoint objects in the `DirectionsResponse` does not contain a `coordinates` property (as per the types). Instead, they contain a `location` property.
        const waypoints = waypointsIncludingStories.slice(1, waypointsIncludingStories.length - 1).map((waypoint) => waypoint.location);

        return waypoints;
    }

    /**
     * Toggle isSnapped for a given waypoint, and update route if snapped
     */
    public async toggleWaypointSnap(waypointId: string, storyKey: string): Promise<void> {
        // Find stories binding waypoint
        const storyIdx = this.stories.findIndex((story) => story.key == storyKey);
        const story = this.stories[storyIdx];
        const prevStory = storyIdx > 0 ? this.stories[storyIdx - 1] : null;

        // Find MoteWaypoint in host story
        const waypoints = story.waypoints;
        const waypoint = waypoints.find((wp) => wp.id == waypointId);

        // Snap/unsnap waypoint
        waypoint.isSnapped = !waypoint.isSnapped;

        // Update route and tour properties
        await this.updateRouteBetweenStories(prevStory, story, waypoints, true);
        await this.updateTourProperties();

        this.changeToSelectMode();
    }

    /**
     * Save an array of waypoints in a story.
     * This operation also generates a new route segment for the story (and a combined route for the tour).
     * @param storyKey Key for the story
     * @param waypoints An array of [number, number] tuples
     * @returns A `Promise` which resolves once the update on the server is complete
     */
    private async updateStoryWaypoints(storyKey: string, waypoints: MoteWaypoint[]): Promise<void> {
        if(!storyKey) return console.warn('Cannot save waypoints: Invalid story key provided:', storyKey);
        if(waypoints?.length == 0) return console.warn('Cannot save waypoints: Invalid or empty array of waypoints:', waypoints);

        // Get the story associated with the waypoint
        const { story, previousStory } = this.getSiblingStories(storyKey);

        // Generate an updated route segment between the story and the previous story
        this.updateRouteBetweenStories(previousStory, story, waypoints || []);

        // Update combined route and related properties
        this.updateTourProperties();

        return this.moteService.touchUpdatedOn(story.key, true);
    }

    /**
     * Delete a `Waypoint` from the map and it's associated story (`Mote`)
     * This operation also generates a new route segment for the story (and a combined route for the tour).
     * @param waypoint The `Waypoint` feature
     * @returns A `Promise` which resolves once the update on the server is complete
     */
    public async deleteWaypoint(waypointId: string, storyKey: string): Promise<void> {
        if(!waypointId) return console.warn('Cannot delete waypoint with a falsy value: ', waypointId);

        // Delete the waypoint from Draw's data store
        this.draw.delete(waypointId);

        // Remove tooltip if any
        const deleteEvent = new WaypointDeletedEvent(waypointId);
        this.sendMapEditorEvent(deleteEvent);

        // Get the story associated with the waypoint (either from story key or waypoint ID)
        const { story, previousStory } = this.getSiblingStories(storyKey);

        // Delete the waypoint from the story
        const waypointIndex = story.waypoints.findIndex((storedWaypoint) => storedWaypoint.id == waypointId);
        story.waypoints.splice(waypointIndex, 1);

        // Generate an updated route segment between the story and the previous story
        this.updateRouteBetweenStories(previousStory, story, story.waypoints || []);

        // Update combined route and related properties
        this.updateTourProperties();

        this.moteService.touchUpdatedOn(story.key, true);

        this.changeToSelectMode();
    }

    /**
     * Combine arrays of route segments and remove any redundant coordinates
     * @param routeSegments An array of arrays of positions
     * @returns The combined and cleaned route
     */
    private combineRouteSegments(routeSegments: [number, number][][]): [number, number][] {
        const combinedRoute = routeSegments.reduce((combined, segment) => {
            return combined.concat(segment);
        }, []);

        // Remove any falsy values
        const combinedAndFilteredRoute = combinedRoute.filter((position) => Boolean(position));
        if(combinedAndFilteredRoute.length <= 0) return [];

        // Remove redundant coordinates: https://turfjs.org/docs/#cleanCoords
        const combinedAndCleanedRoute = cleanCoords(lineString(combinedAndFilteredRoute)).geometry.coordinates;

        return combinedAndCleanedRoute;
    }

    /**
     * Move a story and generate new route segments between the previous, this, and the next story
     */
    private async moveStory(storyKey: string, newPosition: Coords) {
        const { story, previousStory, nextStory } = this.getSiblingStories(storyKey);
        story.position = newPosition;

        await this.moteService.saveMotePosition(story.key, story.position);

        // Update the route between the story and the previous and next story
        await Promise.all([
            this.updateRouteBetweenStories(story, nextStory, nextStory?.waypoints || []),
            this.updateRouteBetweenStories(previousStory, story, story?.waypoints || []),
        ]);
        // Update combined route and related properties
        await this.updateTourProperties();

        // Update address info of moved story
        const addressInfo = await this.geoSearchService.getAddress(story.position);
        const newContent: Partial<MoteData['content']> = {
            address: addressInfo.address,
        };

        if(!story.isCustomTitle) newContent.title = newContent.title || addressInfo?.address;

        await this.moteService.updateMoteContent(story.key, newContent);
        await this.updateMoteCityAndCountry(story.key, addressInfo.city, addressInfo.country);
    }

    /**
     * Update the position of a waypoint and generate a new route segment
     * @param waypointFeature The `FeatureWaypoint`
     * @returns A `Promise` which resolves once the update on the server is complete
     */
    private async moveWaypoint(waypointFeature: FeatureWaypoint): Promise<void> {
        const story = this.stories.find((story) => story.key == waypointFeature.properties.storyKey);
        if(!story) return console.warn('Cannot move waypoint: Associated story does not exist in the active tour');

        const storedWaypoint = story.waypoints.find((waypoint) => waypoint.id == waypointFeature.id);
        storedWaypoint.location = positionToTuple(waypointFeature.geometry.coordinates);

        await this.updateStoryWaypoints(story.key, story.waypoints);

        // Notify
        const moveEvent = new WaypointMovedEvent(storedWaypoint.id, storedWaypoint.location, storedWaypoint.isSnapped, story.key);
        this.sendMapEditorEvent(moveEvent);
    }

    /**
     * Update the `routeGeometry`, `length`, `duration` and `position` properties  of the currently active tour based on the properties of it's contained stories
     * @returns A `Promise` which resolves once the update on the server is complete
     */
    private async updateTourProperties(): Promise<void> {
        const tourKey = this.stories[0]?.parent;

        if(tourKey) {
            const allRouteSegments = this.stories.map((story) => story.routeGeometry);
            const combinedRoute = this.combineRouteSegments(allRouteSegments);
            await this.moteService.saveMoteRouteGeometry(tourKey, combinedRoute);
            // Update tour length and duration
            this.updateTourLengthAndDuration(this.tour);
            // Update center position of tour
            const allStoryCoords = this.stories.map((story) => story.position);
            const centerPosition = Coords.calcAverageCoords(allStoryCoords);
            this.moteService.saveMotePosition(tourKey, centerPosition);
            // If the tour only has one story, also update the `position` property of the `Project`
            // ! This is a temporary fix for providing a reasonable value for this property
            // ! It is currently used in the app when determining the initial map position and when sorting projects
            // TODO: Consider phasing the usage of this property out in favor of the `position` property on individual tours
            if(this.stories?.length == 1) {
                const projectKey = this.stories[0].project;
                const project = await this.projectService.getProject(projectKey);
                if(!project) return console.warn('Cannot update `position` on falsy project:', project);
                // Update the project's `position` property if it does not have a valid coordinate
                if(!project.position || !project.position.isValid) {
                    const position = this.stories[0].position;
                    this.projectService.saveProjectPosition(projectKey, position);
                }
            }
        } else {
            console.warn(`Cannot update combined route geometry: \`parent\` property for stories is not defined.`);
        }
    }

    /**
     * Update the city and country of a story or tour
     */
    private async updateMoteCityAndCountry(moteKey: string, city: string, country: string) {
        this.moteService.saveMoteCity(moteKey, city);
        this.moteService.saveMoteCountry(moteKey, country);
    }

    /**
     * Use route geometry to update tour length and duration
     */
    private async updateTourLengthAndDuration(tour: Mote): Promise<void> {
        let length = tour.getLength(tour.routeGeometry);
        await this.moteService.saveMoteLength(tour.key, length);

        let duration = tour.getEstimatedDuration(this.stories);
        await this.moteService.saveMoteDuration(tour.key, duration);
    }

    /**
     * Delete the currently selected map features
     */
    private deleteSelectedFeatures(): void {
        // Get all features selected on the map
        const selectedFeatures = this.draw.getSelected();
        // If nothing has been selected, return
        if(!selectedFeatures) return console.warn('No features have been selected');
        selectedFeatures.features.forEach((feature) => {
            if(isFeatureWaypoint(feature)) {
                this.deleteWaypoint(String(feature?.id), feature?.properties?.storyKey);
            }
        });
    };

    /**
     * Register hotkeys for common actions in the Map Editor
     */
    private registerHotkeys(): void {
        const mapContainer = this.map.getCanvasContainer();

        // Delete
        AutoSub(this).reg['hotkeyBackspace'] = this.hotkeys
            .add({ element: mapContainer, keys: 'backspace', description: 'Map Editor: Delete' })
            .subscribe(() => this.deleteSelectedFeatures());

        AutoSub(this).reg['hotkeyDelete'] = this.hotkeys
            .add({ element: mapContainer, keys: 'delete', description: 'Map Editor: Delete' })
            .subscribe(() => this.deleteSelectedFeatures());
    };

    /** Clean up, if the service is destroyed */
    ngOnDestroy(): void {
        this.removeMapEventListeners();
        this.map.removeControl(this.draw);
    }
}
